/**
 * The one HTTP client (spec §4.10). Every call unwraps the `{ ok, data }`
 * envelope and throws `ApiError` on failure, so callers only ever see `data`.
 *
 *   const { user } = await api.get<{ user: User }>("/api/auth/me");
 *
 * Cookies are sent with every request (`credentials: "include"`); the browser
 * adds `Origin` itself, which is what the CSRF middleware checks.
 */
import type { ApiResponse, ErrorCode } from "@shared/api";

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly fields: Record<string, string> | undefined;
  readonly requestId: string;

  constructor(init: {
    code: ErrorCode;
    message: string;
    status: number;
    fields?: Record<string, string>;
    requestId?: string;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.code = init.code;
    this.status = init.status;
    this.fields = init.fields;
    this.requestId = init.requestId ?? "unknown";
  }

  /** True for the codes a retry can never fix. */
  get isFatal(): boolean {
    return this.code !== "INTERNAL" && this.code !== "RATE_LIMITED";
  }
}

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function request<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...options.headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    throw new ApiError({
      code: "INTERNAL",
      message: "Network error — check your connection.",
      status: 0,
    });
  }

  const text = await res.text();
  let parsed: ApiResponse<T> | null = null;
  if (text) {
    try {
      parsed = JSON.parse(text) as ApiResponse<T>;
    } catch {
      parsed = null;
    }
  }

  if (parsed && parsed.ok) return parsed.data;
  if (parsed && !parsed.ok) {
    throw new ApiError({
      code: parsed.error.code,
      message: parsed.error.message,
      status: res.status,
      ...(parsed.error.fields ? { fields: parsed.error.fields } : {}),
      requestId: parsed.error.requestId,
    });
  }
  throw new ApiError({
    code: res.status === 404 ? "NOT_FOUND" : "INTERNAL",
    message: `Unexpected response (${res.status})`,
    status: res.status,
  });
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>("GET", path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("POST", path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PUT", path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PATCH", path, body, options),
  delete: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("DELETE", path, body, options),
};

export type Api = typeof api;
